<template>
  <div>
    <!-- Table Container Card -->
    <b-card no-body class="mb-0">
      <div class="m-2">

        <b-row class="align-items-end">
          <!-- Per Page Selector -->
          <b-col cols="12" md="4" lg="3" class="mb-2">
            <div class="d-flex align-items-center">
              <label class="mb-0 mr-1">{{ $t('Show') }}</label>
              <v-select
                  v-model="perPage"
                  :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                  :options="perPageOptions"
                  :clearable="false"
                  class="per-page-selector mx-1"
              />
              <label class="mb-0 ml-1">{{ $t('entries') }}</label>
            </div>
          </b-col>

          <!-- Asset Search -->
          <b-col cols="12" md="8" lg="5" class="mb-2">
            <label class="font-weight-bold d-block">{{ $t('Asset') }}</label>
            <vue-autosuggest
                v-model="assetIdentifier"
                :suggestions="filteredOptions"
                :input-props="inputProps"
                @selected="selectHandler"
                @input="onInputChange"
            >
              <template slot-scope="{ suggestion }">
        <span class="my-suggestion-item">
          {{ suggestion.item.plate || suggestion.item.chassisNumber }}
        </span>
              </template>
            </vue-autosuggest>
          </b-col>

          <!-- Search Input + Add Button -->
          <b-col cols="12" lg="4" class="mb-2">
            <div class="d-flex justify-content-end">
              <b-form-input
                  v-model="searchQuery"
                  :placeholder="$t('Search')"
                  class="mr-2"
              />
              <b-button variant="primary" :to="{ name: 'maintenance-record' }">
                <span class="text-nowrap">{{ $t('Add Maintenance') }}</span>
              </b-button>
            </div>
          </b-col>
        </b-row>


      </div>

      <b-table ref="refMaintenanceRecordListTable" class="position-relative" :items="fetchAllMaintenanceRecord" responsive
               :fields="tableColumns" primary-key="id" :sort-by.sync="sortBy" show-empty :empty-text="emptyText"
               :sort-desc.sync="isSortDirDesc">

        <!-- Column: Identifier -->
        <template #head(chassisNumber)>
          {{ $t('Asset Identifier') }}
        </template>
        <template #cell(chassisNumber)="data">
          <b-link :to="{ name: 'maintenance-record', params: { id: data.item.id }}" class="font-weight-bold ">
            <span class="text-uppercase">{{ data.item.nickname ||  data.item.plate || data.item.chassisNumber}}</span>
          </b-link>
        </template>

        <template #cell(status)="data">
          <b-badge v-if="data.item.endDate" variant="light-success">
            <feather-icon icon="CheckCircleIcon" class="mr-50"/>
            <span class="align-middle">{{ $t('Finished') }}</span>
          </b-badge>
          <b-badge v-else variant="light-warning">
            <feather-icon icon="ClockIcon" class="mr-50"/>
            <span class="align-middle">{{ $t('In progress') }}</span>
          </b-badge>
        </template>


        <!-- Column: startDate -->
        <template #head(startDate)>
          {{ $t('Start Date') }}
        </template>
        <template #cell(startDate)="data">
            <span class="text-uppercase">{{ data.item.startDate}}</span>
        </template>

        <!-- Column: endDate -->
        <template #head(endDate)>
          {{ $t('End Date') }}
        </template>
        <template #cell(endDate)="data">
          <span class="text-uppercase">{{ data.item.endDate}}</span>
        </template>

        <!-- Column: maintenanceDuration -->
        <template #head(maintenanceDuration)>
          {{ $t('Maintenance Duration') }}
        </template>
        <template #cell(invoice)="data">
          <span class="text-uppercase">{{ data.item.maintenanceDuration}}</span>
        </template>

        <!-- Column: odometer -->
        <template #head(odometer)>
          {{ $t('Odometer') }}
        </template>
        <template #cell(odometer)="data">
          <span class="text-uppercase">{{ data.item.odometer}}</span>
        </template>

        <!-- Column: hourmeter -->
        <template #head(hourmeter)>
          {{ $t('Hourmeter') }}
        </template>
        <template #cell(hourmeter)="data">
          <span class="text-uppercase">{{ data.item.hourmeter}}</span>
        </template>

        

        <!-- Column: Actions -->
        <template #head(actions)>
          {{ $t(selectedAssetId ? 'Select group' : 'Actions') }}
        </template>
        <template #cell(actions)="data">
          <b-button variant="primary" v-if="selectedAssetId" @click="bindAssetToGroup(selectedAssetId, data.item.id)">
            <feather-icon icon="CheckSquareIcon"/>
          </b-button>
          <b-dropdown v-else variant="link" no-caret :right="$store.state.appConfig.isRTL">
            <template #button-content>
              <feather-icon icon="MoreVerticalIcon" size="16" class="align-middle text-body"/>
            </template>

            <b-dropdown-item :to="{ name: 'maintenance-record', params: { id: data.item.id } }">
              <feather-icon icon="EditIcon"/>
              <span class="align-middle ml-50">{{ $t('Edit') }}</span>
            </b-dropdown-item>
          </b-dropdown>
        </template>

      </b-table>
      <div class="mx-2 mb-2">
        <b-row>

          <b-col cols="12" sm="6" class="d-flex align-items-center justify-content-center justify-content-sm-start">
            <span class="text-muted"> {{ $t('Showing') }} {{ dataMeta.from }} {{ $t('to') }} {{
                dataMeta.to
              }} {{ $t('of') }} {{ dataMeta.of }} {{ $t('entries') }}</span>
          </b-col>
          <!-- Pagination -->
          <b-col cols="12" sm="6" class="d-flex align-items-center justify-content-center justify-content-sm-end">

            <b-pagination v-model="currentPage" :total-rows="totalMaintenanceRecord" :per-page="perPage" first-number last-number
                          class="mb-0 mt-1 mt-sm-0" prev-class="prev-item" next-class="next-item">
              <template #prev-text>
                <feather-icon icon="ChevronLeftIcon" size="18"/>
              </template>
              <template #next-text>
                <feather-icon icon="ChevronRightIcon" size="18"/>
              </template>
            </b-pagination>

          </b-col>

        </b-row>
      </div>
    </b-card>
    
  </div>


</template>

<script>


import {
  BCard,
  BRow,
  BCol,
  BFormInput,
  BButton,
  BTable,
  BMedia,
  BAvatar,
  BLink,
  BBadge,
  BDropdown,
  BDropdownItem,
  BPagination,
  BCardHeader,
  BCardBody,
  BModal
} from 'bootstrap-vue'

import {VueAutosuggest} from 'vue-autosuggest'

import store from '@/store'
import {
  ref,
  onUnmounted
} from '@vue/composition-api'

import managementStoreModule from '../managementStoreModule'
import vSelect from 'vue-select'
import flatPickr from 'vue-flatpickr-component'
import useMaintenanceRecordList from "@/views/main/management/maintenanceRecord/useMaintenanceRecordList";

export default {
  props: ["selectedAssetId"],
  components: {
    //UsersListFilters,

    BCard,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BTable,
    BMedia,
    BAvatar,
    BLink,
    BBadge,
    BDropdown,
    BDropdownItem,
    BPagination,
    BCardHeader,
    BCardBody,
    BModal,
    vSelect,
    flatPickr,
    VueAutosuggest


  },
  data() {
    return {
    
      emptyText: this.$t('No matching records found'),
      showModal: false,
      asset: {
        name: '',
        id: ''
      },

      datasuggest: [],
      filteredOptions: [],
      inputProps: {
        id: 'autosuggest__input',
        class: 'form-control',
        placeholder: this.$t('Search for your asset ...'),
      },
      limit: 5,
      groupId: ''
    }
  },
  methods: {

    selectHandler(option) {
      this.assetId = option.item.id
      this.assetIdentifier = option.item.nickname || option.item.plate || option.item.chassisNumber
      this.filteredOptions = []
    },

    onInputChange(text) {
      if (text === '' || text === undefined || text.length < 3) {
        this.assetId = null
        this.assetIdentifier = null
        return
      }

      store.dispatch('app-management/fetchAssets', {
        q: text,
        excludeGrouped: true
      })
          .then(response => {

            this.filteredOptions = [{
              data: response.data.data,
            }]

          })
          .catch(() => {})
    },

  },
  setup() {
    const MANAGEMENT_APP_STORE_MODULE_NAME = 'app-management'
    const assetIdentifier = ref('')

    // Register module
    if (!store.hasModule(MANAGEMENT_APP_STORE_MODULE_NAME)) store.registerModule(MANAGEMENT_APP_STORE_MODULE_NAME, managementStoreModule)

    // UnRegister on leave
    onUnmounted(() => {
      if (store.hasModule(MANAGEMENT_APP_STORE_MODULE_NAME)) store.unregisterModule(MANAGEMENT_APP_STORE_MODULE_NAME)
    })

    const {
      fetchAllMaintenanceRecord,
      tableColumns,
      perPage,
      currentPage,
      totalMaintenanceRecord,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refMaintenanceRecordListTable,
      refetchData,
      assetId
    } = useMaintenanceRecordList()

    return {
      fetchAllMaintenanceRecord,
      tableColumns,
      perPage,
      currentPage,
      totalMaintenanceRecord,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refMaintenanceRecordListTable,
      refetchData,
      assetId,
      MANAGEMENT_APP_STORE_MODULE_NAME,
      assetIdentifier
    }
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
@import '@core/scss/vue/libs/vue-autosuggest.scss';
@import '@core/scss/vue/libs/vue-flatpicker.scss';

.per-page-selector {
  width: 90px;
}
</style>
